import type { OverrideValue } from '../channel/ChannelSettingDropdown';
import type { DBChannel } from '@core/types/types.db';
import type { SlackSetChannelPromptEndpoint } from '@core/types/types.endpoint.slack';
import { callApi } from '@frontend/shared/util/util.callApi';

export interface ChannelSettingsFormState {
  instructionsOverride: boolean;
  instructionsValue: string;
  contextOverride: boolean;
  contextValue: string;
  questionsToIgnoreOverride: boolean;
  questionsToIgnoreValue: string;
  teamContextToggle: boolean;
  supportModeValue: OverrideValue;
  respondToBotMessagesValue: OverrideValue;
  aiAnswerValue: OverrideValue;
  recencyBiasValue: OverrideValue;
  categorySelectionType: 'all' | 'none' | 'specific';
  selectedCategories: string[];
  defaultCategoryOverride: boolean;
  defaultCategoryValue: string;
  notFoundLabelOverride: boolean;
  supportMessageNotFoundValue: string;
  notHelpfulLabelOverride: boolean;
  supportMessageNotHelpfulValue: string;
}

export function buildChannelSettingsPayload(
  formState: ChannelSettingsFormState,
  teamDefault: string,
): SlackSetChannelPromptEndpoint['body'] & { channelId: string } {
  const {
    instructionsOverride,
    instructionsValue,
    contextOverride,
    contextValue,
    questionsToIgnoreOverride,
    questionsToIgnoreValue,
    teamContextToggle,
    supportModeValue,
    respondToBotMessagesValue,
    aiAnswerValue,
    recencyBiasValue,
    categorySelectionType,
    selectedCategories,
    defaultCategoryOverride,
    defaultCategoryValue,
    notFoundLabelOverride,
    supportMessageNotFoundValue,
    notHelpfulLabelOverride,
    supportMessageNotHelpfulValue,
  } = formState;

  // Handle instructions override
  let promptToSet: string | undefined;
  if (instructionsOverride) {
    const trimmedPrompt = instructionsValue.trim();
    // If it matches team default, don't set an override
    promptToSet = trimmedPrompt === teamDefault ? undefined : trimmedPrompt;
  } else {
    promptToSet = undefined;
  }

  // Handle context override
  const contextToSet =
    contextOverride && contextValue.trim() ? contextValue.trim() : null;

  // Handle questions to ignore override
  const questionsToIgnoreToSet =
    questionsToIgnoreOverride && questionsToIgnoreValue.trim()
      ? questionsToIgnoreValue.trim()
      : null;

  // Handle support mode override
  let supportModeOverrideToSet: boolean | null | undefined;
  if (supportModeValue === 'global') {
    supportModeOverrideToSet = null;
  } else if (supportModeValue === 'enabled') {
    supportModeOverrideToSet = true;
  } else if (supportModeValue === 'disabled') {
    supportModeOverrideToSet = false;
  }

  // Handle respond to bot messages override
  let respondToBotMessagesOverrideToSet: boolean | null | undefined;
  if (respondToBotMessagesValue === 'global') {
    respondToBotMessagesOverrideToSet = null;
  } else if (respondToBotMessagesValue === 'enabled') {
    respondToBotMessagesOverrideToSet = true;
  } else if (respondToBotMessagesValue === 'disabled') {
    respondToBotMessagesOverrideToSet = false;
  }

  // Handle ai answer override
  let aiAnswerOverrideToSet: boolean | null | undefined;
  if (aiAnswerValue === 'global') {
    aiAnswerOverrideToSet = null;
  } else if (aiAnswerValue === 'enabled') {
    aiAnswerOverrideToSet = true;
  } else if (aiAnswerValue === 'disabled') {
    aiAnswerOverrideToSet = false;
  }

  // Handle recency bias override
  let recencyBiasOverrideToSet: boolean | null | undefined;
  if (recencyBiasValue === 'global') {
    recencyBiasOverrideToSet = null;
  } else if (recencyBiasValue === 'enabled') {
    recencyBiasOverrideToSet = true;
  } else if (recencyBiasValue === 'disabled') {
    recencyBiasOverrideToSet = false;
  }

  // Handle label overrides
  let labelsToSet:
    | {
        support_message_not_found?: string;
        support_message_not_helpful?: string;
      }
    | null
    | undefined;

  if (notFoundLabelOverride || notHelpfulLabelOverride) {
    labelsToSet = {};
    if (notFoundLabelOverride) {
      labelsToSet.support_message_not_found =
        supportMessageNotFoundValue.trim();
    }
    if (notHelpfulLabelOverride) {
      labelsToSet.support_message_not_helpful =
        supportMessageNotHelpfulValue.trim();
    }
  } else {
    labelsToSet = null;
  }

  // Handle category source settings
  let categoryIdsToSet: string[] | null = null;
  if (categorySelectionType === 'all') {
    categoryIdsToSet = null;
  } else if (categorySelectionType === 'none') {
    categoryIdsToSet = [];
  } else if (categorySelectionType === 'specific') {
    categoryIdsToSet = selectedCategories;
  }

  // Handle default category
  const defaultCategoryToSet = defaultCategoryOverride
    ? defaultCategoryValue
    : null;

  return {
    channelId: '', // Will be filled by caller
    prompt: promptToSet,
    prompt_context: contextToSet,
    prompt_classify_ignored: questionsToIgnoreToSet,
    team_context_disabled: teamContextToggle,
    support_mode_override: supportModeOverrideToSet,
    respond_to_bot_messages_override: respondToBotMessagesOverrideToSet,
    ai_answer_override: aiAnswerOverrideToSet,
    recency_bias_override: recencyBiasOverrideToSet,
    labels: labelsToSet,
    answer_source_category_ids: categoryIdsToSet,
    default_category_id: defaultCategoryToSet,
  };
}

export async function saveChannelSettings(
  channelId: string,
  formState: ChannelSettingsFormState,
  teamDefault: string,
): Promise<DBChannel | null> {
  const payload = buildChannelSettingsPayload(formState, teamDefault);
  payload.channelId = channelId;

  const [, res] = await callApi<SlackSetChannelPromptEndpoint>(
    '/slack/setChannelPrompt',
    payload,
  );

  return res?.channel ?? null;
}
